import { useTranslate, HttpError } from '@refinedev/core';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, Radio, Tabs, Divider } from 'antd';
import type { PublisherDetailOut, PublisherIn } from '@/api/generated/schemas';
import { ModStatus } from '@/api/generated/schemas';
import { CollectionImageFields } from '@/components/common/CollectionImageFields';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';
import {
  CollectionSortingList,
  normalizeCollectionSorting,
} from '@/components/query-builder';

export const PublisherCreate = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps } = useForm<
    PublisherDetailOut,
    HttpError,
    PublisherIn
  >({
    resource: 'user_catalog.publishers',
  });

  const handleFinish = (values: PublisherIn) =>
    formProps.onFinish?.({
      ...values,
      publication_custom_sorting: normalizeCollectionSorting(
        values.publication_custom_sorting
      ),
    });

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical" onFinish={handleFinish}>
        <Tabs
          items={[
            {
              key: 'basic',
              label: t('base.sections.basic_info'),
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t('user_catalog.publishers.fields.name')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="description"
                    label={t('user_catalog.publishers.fields.description')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <Divider>{t('base.sections.system_info')}</Divider>

                  <Form.Item
                    name="mod_status"
                    label={t('base.fields.mod_status')}
                    rules={[{ required: true }]}
                    initialValue={ModStatus.enabled}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ModStatus).map((value) => ({
                        label: t(`base.mod_status.${value}`),
                        value,
                      }))}
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'collection',
              label: t('base.sections.collection_info'),
              children: (
                <>
                  <Form.Item
                    name="header"
                    label={t('user_catalog.publishers.fields.header')}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="content"
                    label={t('user_catalog.publishers.fields.content')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <CollectionImageFields />

                  <Divider>{t('base.collection.sorting')}</Divider>
                  <Form.Item extra={t('base.collection.sorting_hint')}>
                    <CollectionSortingList />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'search',
              label: t('user_catalog.common.tabs.search'),
              children: <SearchPopularityField />,
            },
          ]}
        />
      </Form>
    </Create>
  );
};
